import hashString from '../helpers/hashString';
import { normalizeName, normalizeEmail, normalizeAddressPart } from './gtmNormalizationHelpers';
interface BasicUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  zip?: string;
  businessLine?: string;
}
interface HashedUserData {
  firstName?: string;
  firstName_no_punctuation?: string;
  lastName?: string;
  lastName_no_punctuation?: string;
  email?: string;
  email_no_dots?: string;
  zip?: string;
}

/**
 * Normalizes and hashes user registration data for analytics tracking
 * Automatically includes specialized variants (no punctuation, no dots) when source data is available
 *
 * CHILD ACCOUNT PROTECTION:
 * For child accounts (businessLine === 'Kids'), PII fields (firstName, lastName)
 * are excluded from GTM tracking to comply with children's privacy regulations.
 *
 * @param userData User registration data to be normalized and hashed
 * @returns Object with hashed user data values
 */

export const gtmPrepareRegistrationData = async (userData: BasicUserData): Promise<HashedUserData> => {
  const {
    firstName,
    lastName,
    email,
    zip,
    businessLine
  } = userData;
  const isChildAccount = businessLine?.toLowerCase() === 'kids';
  const normalizedData: Record<string, string | undefined> = {};
  if (firstName && !isChildAccount) {
    const firstNameNoPunct = normalizeName(firstName, true);
    normalizedData.firstName = normalizeName(firstName);
    normalizedData.firstName_no_punctuation = firstNameNoPunct;
    normalizedData.firstNameNoPunctuation = firstNameNoPunct;
  }
  if (lastName && !isChildAccount) {
    const lastNameNoPunct = normalizeName(lastName, true);
    normalizedData.lastName = normalizeName(lastName);
    normalizedData.lastName_no_punctuation = lastNameNoPunct;
    normalizedData.lastNameNoPunctuation = lastNameNoPunct;
  }
  if (email) {
    const emailNoDots = normalizeEmail(email, true);
    normalizedData.email = normalizeEmail(email);
    normalizedData.email_no_dots = emailNoDots;
    normalizedData.emailNoDots = emailNoDots;
  }
  if (zip) {
    normalizedData.zip = normalizeAddressPart(zip);
  }
  const fieldsToHash = Object.entries(normalizedData);
  const hashPromises = fieldsToHash.map(([, value]) => hashString(value));
  const hashedValues = await Promise.all(hashPromises);
  const result: HashedUserData = {};
  fieldsToHash.forEach(([fieldName], index) => {
    result[fieldName as keyof HashedUserData] = hashedValues[index];
  });
  return result;
};